import { z } from 'zod';

// Zod schema coercing backend numeric strings (and numbers) to number at
// the parse boundary — for USD prices, percentages, and small display
// floats. Behaves like z.coerce.number() plus a dev tripwire: on localhost
// (a compile-time define) it console.warns when the coerced magnitude
// exceeds Number.MAX_SAFE_INTEGER — the signal that a wei-scale value got
// stuck in a float field; the value still parses (precision is already
// lost) — wei-denominated fields belong as plain strings or
// z.coerce.bigint(), never here. Non-numeric input fails the parse.
//
// ⚠️ Number(null) === 0: a bare safeFloat silently coerces null to 0
// instead of failing — fields that are legitimately null must use
// safeFloat.nullable(), which preserves null.
export const safeFloat: z.ZodType<number> = z.unknown().transform((v, ctx) => {
  const n = Number(v);
  if (Number.isNaN(n)) {
    ctx.addIssue({
      code: 'custom',
      message: `Expected numeric, got ${typeof v}`,
    });
    return z.NEVER;
  }
  if (
    import.meta.env.VITE_IS_LOCALHOST &&
    Math.abs(n) > Number.MAX_SAFE_INTEGER
  ) {
    // eslint-disable-next-line no-console -- localhost dev tripwire
    console.warn(
      `[schema] safeFloat value ${String(v)} exceeds Number.MAX_SAFE_INTEGER — precision lost. Use z.coerce.bigint() or keep as string.`,
    );
  }
  return n;
});
